import { useFormik } from "formik";
import { InvoiceInput } from "../types/interfaces";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TextField } from "@mui/material";
import React, { useState } from "react";
import * as yup from 'yup';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useMutation } from "@apollo/client";
import { GENERATE_INVOICE } from "../graphql/mutation";
import { downloadPdfFromBase64 } from "../utils/downloadPdf";
import TaskLineTypeFields, { taskLineYupSchema, emptyTaskLine, useDefaultLineType } from './TaskLineTypeFields';

const validationSchema = yup.object({
  customerName: yup.string().required('Customer name is required'),
  customerABN: yup.string().required('Customer ABN is required'),
  customerAddress: yup.string().required('Customer address is required'),
  customerEmail: yup.string().email('Must be a valid email').optional(),
  projectName: yup.string().required('Project name is required'),
  projectDescription: yup.string().required('Project description is required'),
  notes: yup.string().optional(),
  taxRate: yup.number().optional().min(0, 'Tax rate cannot be negative').max(100, 'Tax rate cannot exceed 100%'),
  tasks: yup.array().of(taskLineYupSchema).min(1, 'At least one task is required'),
  discount: yup.number().optional().min(0, 'Discount cannot be negative').max(100, 'Discount cannot exceed 100%'),
});

interface CreateInvoiceDialogProps {
  open: boolean;
  onClose: () => void;
  onInvoiceCreated: () => void;
  onError: (message: string) => void;
}

const CreateInvoiceDialog: React.FC<CreateInvoiceDialogProps> = ({
  open,
  onClose,
  onInvoiceCreated,
  onError
}) => {
  const [generateInvoice] = useMutation(GENERATE_INVOICE);
  const [isGenerating, setIsGenerating] = useState(false);
  const defaultLineType = useDefaultLineType();
  const formik = useFormik<InvoiceInput>({
    enableReinitialize: true,
    initialValues: {
      customerName: '',
      customerABN: '',
      customerAddress: '',
      customerEmail: '',
      projectName: '',
      projectDescription: '',
      notes: '',
      taxRate: 10,
      tasks: [emptyTaskLine(defaultLineType)],
      discount: 0,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsGenerating(true);
      try {
        // Transform taxRate from percentage (10) to decimal (0.10) before submitting
        const submissionValues = {
          ...values,
          taxRate: (values.taxRate ?? 10) / 100,
          // Remove empty optional fields so the backend uses defaults
          customerEmail: values.customerEmail || undefined,
          notes: values.notes || undefined,
        };

        const { data } = await generateInvoice({
          variables: { input: submissionValues },
        });

        if (data?.generateInvoice) {

          // Generate filename using customer name and date
          const filename = `Invoice_${values.customerName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;

          // Use the download utility
          downloadPdfFromBase64(data.generateInvoice, filename);

          onInvoiceCreated();
        }
        onClose();
      } catch (error) {
        onError(error instanceof Error ? error.message : 'Failed to generate invoice');
      } finally {
        setIsGenerating(false);
      }
    },
  });

  const handleAddTask = () => {
    formik.setFieldValue('tasks', [
      ...formik.values.tasks,
      emptyTaskLine(defaultLineType),
    ]);
  };

  const handleRemoveTask = (index: number) => {
    const tasks = [...formik.values.tasks];
    tasks.splice(index, 1);
    formik.setFieldValue('tasks', tasks);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate New Invoice</DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid size={6}>
              <TextField
                fullWidth
                id="customerName"
                name="customerName"
                label="Customer Name"
                value={formik.values.customerName}
                onChange={formik.handleChange}
                error={formik.touched.customerName && Boolean(formik.errors.customerName)}
                helperText={formik.touched.customerName && formik.errors.customerName}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                id="customerABN"
                name="customerABN"
                label="Customer ABN"
                value={formik.values.customerABN}
                onChange={formik.handleChange}
                error={formik.touched.customerABN && Boolean(formik.errors.customerABN)}
                helperText={formik.touched.customerABN && formik.errors.customerABN}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                id="customerAddress"
                name="customerAddress"
                label="Customer Address"
                multiline
                rows={2}
                value={formik.values.customerAddress}
                onChange={formik.handleChange}
                error={formik.touched.customerAddress && Boolean(formik.errors.customerAddress)}
                helperText={formik.touched.customerAddress && formik.errors.customerAddress}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                id="customerEmail"
                name="customerEmail"
                label="Customer Email"
                type="email"
                value={formik.values.customerEmail}
                onChange={formik.handleChange}
                error={formik.touched.customerEmail && Boolean(formik.errors.customerEmail)}
                helperText={formik.touched.customerEmail && formik.errors.customerEmail}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                id="projectName"
                name="projectName"
                label="Project Name"
                value={formik.values.projectName}
                onChange={formik.handleChange}
                error={formik.touched.projectName && Boolean(formik.errors.projectName)}
                helperText={formik.touched.projectName && formik.errors.projectName}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                id="projectDescription"
                name="projectDescription"
                label="Project Description"
                value={formik.values.projectDescription}
                onChange={formik.handleChange}
                error={formik.touched.projectDescription && Boolean(formik.errors.projectDescription)}
                helperText={formik.touched.projectDescription && formik.errors.projectDescription}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                id="discount"
                name="discount"
                label="Discount (%)"
                type="number"
                value={formik.values.discount}
                onChange={formik.handleChange}
                error={formik.touched.discount && Boolean(formik.errors.discount)}
                helperText={formik.touched.discount && formik.errors.discount}
              />
            </Grid>
            <Grid size={6}>
              <TextField
                fullWidth
                id="taxRate"
                name="taxRate"
                label="Tax Rate / GST (%)"
                type="number"
                value={formik.values.taxRate}
                onChange={formik.handleChange}
                error={formik.touched.taxRate && Boolean(formik.errors.taxRate)}
                helperText={formik.touched.taxRate && formik.errors.taxRate}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                id="notes"
                name="notes"
                label="Notes (optional)"
                multiline
                rows={2}
                value={formik.values.notes}
                onChange={formik.handleChange}
                error={formik.touched.notes && Boolean(formik.errors.notes)}
                helperText={formik.touched.notes && formik.errors.notes}
              />
            </Grid>
            {formik.values.tasks.map((task, index) => (
              <React.Fragment key={index}>
                <Grid size={12}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <h4>Task {index + 1}</h4>
                    {index > 0 && (
                      <IconButton onClick={() => handleRemoveTask(index)}>
                        <RemoveIcon />
                      </IconButton>
                    )}
                  </Box>
                </Grid>
                <Grid size={6}>
                  <TextField
                    fullWidth
                    id={`tasks[${index}].name`}
                    name={`tasks[${index}].name`}
                    label="Task Name"
                    value={task.name}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.tasks?.[index]?.name &&
                      Boolean((formik.errors.tasks?.[index] as any)?.name)
                    }
                    helperText={
                      formik.touched.tasks?.[index]?.name &&
                      (formik.errors.tasks?.[index] as any)?.name
                    }
                  />
                </Grid>
                <TaskLineTypeFields
                  index={index}
                  lineType={task.lineType ?? 'HOURLY'}
                  duration={task.duration ?? 0}
                  amount={task.amount ?? 0}
                  onChange={formik.handleChange}
                  touched={formik.touched.tasks?.[index]}
                  errors={formik.errors.tasks?.[index] as any}
                />
                <Grid size={12}>
                  <TextField
                    fullWidth
                    id={`tasks[${index}].description`}
                    name={`tasks[${index}].description`}
                    label="Description"
                    value={task.description}
                    onChange={formik.handleChange}
                    error={
                      formik.touched.tasks?.[index]?.description &&
                      Boolean((formik.errors.tasks?.[index] as any)?.description)
                    }
                    helperText={
                      formik.touched.tasks?.[index]?.description &&
                      (formik.errors.tasks?.[index] as any)?.description
                    }
                  />
                </Grid>
              </React.Fragment>
            ))}

            <Grid size={12}>
              <Button
                startIcon={<AddIcon />}
                onClick={handleAddTask}
                variant="outlined"
              >
                Add Task
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" color="primary" variant="contained" disabled={isGenerating || !formik.isValid}>
             {isGenerating ? (
                <>
                  <CircularProgress size={24} color="inherit" sx={{ mr: 1 }} />
                  Generating...
                </>
              ) : (
                'Generate Invoice'
              )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateInvoiceDialog;
